import { GraphQLResult } from "@aws-amplify/api-graphql";
import { useQuery } from "@tanstack/react-query";
import {
  AppointmentFileReferenceStatus,
  AppointmentRecord,
  FileProvider,
  FileType,
  UploadedFilesBySourceAppointmentIDQuery,
} from "~/api/API";
import { uploadedFilesBySourceAppointmentID } from "~/api/graphql/queries";
import { amplifyGraphQLClient } from "~/lib/amplify-client";
import { uploadedFileService } from "~/services/uploadedFile";

export interface FileDisplayData {
  id: string;
  fileName: string;
  fileType: FileType;
  providedBy: FileProvider;
  uploaded: {
    dateTime: Date;
    user: string;
  };
  key: string;
  /** Size in bytes of the S3 object; null on rows not yet backfilled */
  fileSizeBytes: number | null;
  actions: string;
  appointmentID?: string | null;
  /** Whether this file is read-only (inherited from another appointment) */
  isReadOnly: boolean;
  /** The reference status of this file relative to the queried appointment */
  referenceStatus: AppointmentFileReferenceStatus;
}

// Query keys for appointment files - export for use in invalidation
export const appointmentFilesKeys = {
  all: ["appointmentFiles"] as const,
  detail: (appointmentId: string) =>
    [...appointmentFilesKeys.all, appointmentId] as const,
  /**
   * Cache key for the source-appointment GSI read path. Nests under detail()
   * so existing `invalidateQueries({ queryKey: detail(id) })` calls (which
   * prefix-match) refresh it too, while keeping it distinct from the junction
   * read cached at detail(id) — the two paths return different inheritance
   * shapes and must not collide.
   */
  bySource: (appointmentId: string) =>
    [...appointmentFilesKeys.detail(appointmentId), "bySource"] as const,
  /** Disabled query key - used when appointmentId is not provided */
  disabled: ["appointmentFiles", "disabled"] as const,
};

// ============================================================================
// File Filters - Use these with the `select` option or directly on file arrays
// ============================================================================

/**
 * Filter that excludes billing receipt files.
 * Use with useAppointmentFiles select option or directly on file arrays.
 */
export const excludeBillingReceiptsFilter = (
  files: FileDisplayData[],
): FileDisplayData[] =>
  files.filter((file) => file.fileType !== FileType.BILLING_RECEIPT);

/**
 * Filter that includes ONLY billing receipt files.
 * Use with useAppointmentFiles select option or directly on file arrays.
 */
export const onlyBillingReceiptsFilter = (
  files: FileDisplayData[],
): FileDisplayData[] =>
  files.filter((file) => file.fileType === FileType.BILLING_RECEIPT);

// ============================================================================
// Shared Query Config Factory
// ============================================================================

/**
 * Creates the React Query config for fetching files for a single appointment.
 * Used by both useAppointmentFiles (useQuery) and useLinkedAppointmentFiles (useQueries)
 * to share cache keys and transformation logic.
 *
 * Files with ORIGINAL reference status get appointmentID set to the queried
 * appointment, ensuring correct ownership attribution in linked-appointment views.
 */
export function appointmentFilesQueryConfig(appointmentId: string) {
  return {
    queryKey: appointmentFilesKeys.detail(appointmentId),
    queryFn: async (): Promise<FileDisplayData[]> => {
      const result = await uploadedFileService.getFilesByAppointment({
        appointmentId,
      });

      return result
        .filter((file) => file.key !== null)
        .map((file) => ({
          id: file.id,
          fileName: file.fileName ?? file.key?.split("/").at(-1) ?? "",
          fileType: file.type ?? FileType.UNDETERMINED,
          providedBy: file.provider ?? FileProvider.UNSPECIFIED,
          uploaded: {
            dateTime: new Date(file.uploadedAt ?? new Date().toISOString()),
            user: file.uploadedBy ?? "",
          },
          key: file.key ?? "",
          fileSizeBytes: file.fileSizeBytes,
          actions: file.key ?? "",
          appointmentID:
            file.referenceStatus ===
            AppointmentFileReferenceStatus.INHERITED_READ_ONLY
              ? file.sourceAppointmentId
              : appointmentId,
          isReadOnly:
            file.referenceStatus ===
            AppointmentFileReferenceStatus.INHERITED_READ_ONLY,
          referenceStatus:
            file.referenceStatus ?? AppointmentFileReferenceStatus.ORIGINAL,
        }));
    },
    staleTime: 5 * 60 * 1000,
  };
}

/**
 * Creates the React Query config for fetching files for a single appointment
 * via the `UploadedFile.bySourceAppointment` GSI (read-time inheritance model),
 * instead of the AppointmentFileReference junction table.
 *
 * Mirrors the backend `_get_files_by_source_appointment_id`: returns only the
 * files whose `sourceAppointmentID` equals this appointment, each marked
 * ORIGINAL (and therefore not read-only). Cross-appointment inheritance —
 * which of these surface as read-only on a *related* appointment — is derived
 * downstream by resolveFileOwnership over the full reschedule/linked family,
 * so this config intentionally does no inheritance resolution itself.
 *
 * Gated behind FEATURE_FLAGS.PORTAL_READ_FILES_BY_SOURCE_APPOINTMENT; only the
 * linked-appointment read path (useAllAppointmentFiles) selects it.
 */
export function sourceAppointmentFilesQueryConfig(appointmentId: string) {
  return {
    queryKey: appointmentFilesKeys.bySource(appointmentId),
    queryFn: async (): Promise<FileDisplayData[]> => {
      const files: FileDisplayData[] = [];
      let nextToken: string | null | undefined = undefined;

      do {
        const result =
          (await amplifyGraphQLClient.graphql<UploadedFilesBySourceAppointmentIDQuery>(
            {
              query: uploadedFilesBySourceAppointmentID,
              variables: {
                sourceAppointmentID: appointmentId,
                limit: 100,
                nextToken,
              },
            },
          )) as GraphQLResult<UploadedFilesBySourceAppointmentIDQuery>;

        const connection = result.data?.uploadedFilesBySourceAppointmentID;
        for (const file of connection?.items ?? []) {
          if (!file || file.key == null) continue;
          files.push({
            id: file.id,
            fileName: file.fileName ?? file.key.split("/").at(-1) ?? "",
            fileType: file.type ?? FileType.UNDETERMINED,
            providedBy: file.provider ?? FileProvider.UNSPECIFIED,
            uploaded: {
              dateTime: new Date(file.uploadedAt ?? new Date().toISOString()),
              user: file.uploadedBy ?? "",
            },
            key: file.key,
            fileSizeBytes: file.fileSizeBytes ?? null,
            actions: file.key,
            // Every file is ORIGINAL relative to its own source appointment;
            // read-only is resolved across the family by resolveFileOwnership.
            appointmentID: appointmentId,
            isReadOnly: false,
            referenceStatus: AppointmentFileReferenceStatus.ORIGINAL,
          });
        }

        nextToken = connection?.nextToken;
      } while (nextToken);

      return files;
    },
    staleTime: 5 * 60 * 1000,
  };
}

// ============================================================================
// Main Hook
// ============================================================================

const EMPTY_FILES: FileDisplayData[] = [];

interface UseAppointmentFilesOptions {
  /**
   * Transform/filter the cached files for this specific consumer.
   * The cache stores ALL files; use select to get a filtered view.
   *
   * @example
   * // Exclude billing receipts (default behavior for UploadTab)
   * useAppointmentFiles(appointment, { select: excludeBillingReceiptsFilter })
   *
   * // Only billing receipts (for BillingTab)
   * useAppointmentFiles(appointment, { select: onlyBillingReceiptsFilter })
   *
   * // Custom filter
   * useAppointmentFiles(appointment, {
   *   select: (files) => files.filter(f => f.fileType === FileType.MEDICAL_RECORD)
   * })
   */
  select?: (files: FileDisplayData[]) => FileDisplayData[];
}

interface UseAppointmentFilesResult {
  /** List of files for the appointment (filtered if select option provided) */
  files: FileDisplayData[];
  /** Whether files are currently being loaded (first fetch, no cached data) */
  isLoading: boolean;
  /**
   * Whether a fetch is in flight, including background refetches where cached
   * (stale) data is still being served. Use this — not `isLoading` — when you
   * need to know the served `files` reflect the latest server state.
   */
  isFetching: boolean;
  /** Error if file loading failed */
  error: Error | null;
  /** Refetch files from the server */
  refetch: () => Promise<void>;
}

/**
 * Hook for loading appointment files using React Query.
 *
 * ## Cache Architecture
 * The cache stores ALL files for an appointment. Filtering happens at consumption
 * time via the `select` option, allowing different components to have different
 * views of the same cached data:
 *
 * ```
 * ┌─────────────────────────────────────────────────┐
 * │  React Query Cache                              │
 * │  ["appointmentFiles", "abc-123"]                │
 * │  [file1, file2, billingReceipt1, ...]           │
 * └─────────────────────────────────────────────────┘
 *         │                           │
 *         ▼                           ▼
 *    UploadTab                   BillingTab
 *    select: excludeBilling      select: onlyBilling
 * ```
 *
 * ## Invalidation
 * Files can be invalidated from anywhere using:
 * ```
 * queryClient.invalidateQueries({
 *   queryKey: appointmentFilesKeys.detail(appointmentId)
 * });
 * ```
 *
 * @param appointment - The appointment to load files for
 * @param options - Configuration options including select for filtering
 */
export function useAppointmentFiles(
  appointment: AppointmentRecord | null | undefined,
  options: UseAppointmentFilesOptions = {},
): UseAppointmentFilesResult {
  const { select } = options;

  const appointmentId = appointment?.id;

  const {
    data: files = EMPTY_FILES,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    ...(appointmentId
      ? appointmentFilesQueryConfig(appointmentId)
      : { queryKey: appointmentFilesKeys.disabled, queryFn: () => [] }),
    // Filter at consumption time, not storage time
    select,
    enabled: !!appointmentId,
  });

  return {
    files,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch: async () => {
      await refetch();
    },
  };
}

// ============================================================================
// Convenience Hooks - Pre-configured for common use cases
// ============================================================================

/**
 * Hook for UploadTab - returns all files EXCEPT billing receipts.
 * This is the most common use case for file display.
 */
export function useUploadTabFiles(
  appointment: AppointmentRecord | null | undefined,
): UseAppointmentFilesResult {
  return useAppointmentFiles(appointment, {
    select: excludeBillingReceiptsFilter,
  });
}

/**
 * Hook for BillingTab - returns ONLY billing receipt files.
 */
export function useBillingFiles(
  appointment: AppointmentRecord | null | undefined,
): UseAppointmentFilesResult {
  return useAppointmentFiles(appointment, {
    select: onlyBillingReceiptsFilter,
  });
}
